<template>
    <div :class="navbarClasses" :style="navFrameStyle">
        <div class="navbar-header">
            <div v-if="showLogo" class="navbar-logo-container">
                <PortfolioNavigationLogo
                    :logo="resolvedLogo"
                    :user-name="resolvedUserName"
                    :logo-class="logoClass"
                    @navigate-home="navigateToHome"
                />
            </div>
            <HamburgerToggle v-if="showHamburger" />
        </div>

        <div :class="['navbar-collapse', navOpenClass, noMobileClass]">
            <div
                ref="navbarContainer"
                :class="['navbar-container', `nav-${theme}`, noMobileClass]"
                :style="navItemGapStyle"
            >
                <div v-if="showLines" class="line"></div>

                <AnimationWrapper
                    v-for="(page, index) in filteredPages"
                    :key="page._id || `${page.shortId}-${index}`"
                    :animation-style="animationSettings?.animationStyle !== 'NONE' ? 'FADE_IN' : null"
                    :delay-animation="index * 0.1"
                    :animate-on-mount="$store.getters.heroIsSidebar"
                >
                    <template #animationContent>
                        <PortfolioNavigationItem
                            :page="page"
                            :index="index"
                            :button-style="buttonStyleClass"
                            :upper-case="upperCase"
                            :show-separator="showSeparator(index)"
                        />
                    </template>
                </AnimationWrapper>

                <PortfolioNavigationSocialLinks
                    v-if="showSocialLinks"
                />

                <div v-if="showLines" class="line"></div>
            </div>

            <div v-if="showCtaButton" class="navbar-item navbar-cta">
                <CtaButton
                    :section="navigation"
                    :form-submit-button="false"
                />
            </div>
        </div>
    </div>
</template>

<script>
import PortfolioNavigationLogo from "@/scripts/sections/common/navigation/PortfolioNavigationLogo.vue";
import PortfolioNavigationItem from "./PortfolioNavigationItem.vue";
import PortfolioNavigationSocialLinks from "./PortfolioNavigationSocialLinks.vue";
import HamburgerToggle from "@/scripts/view/components/HamburgerToggle.vue";
import AnimationWrapper from "@/scripts/common/components/AnimationWrapper.vue";
import CtaButton from "@/scripts/view/portfolio/sections/_helper/CtaButton.vue";
import stickyNavigationMixin from "@/scripts/sections/common/navigation/stickyNavigationMixin.js";
import navigationWrapObserverMixin from "@/scripts/sections/common/navigation/navigationWrapObserverMixin.js";
import navigationPresentationMixin from "@/scripts/sections/common/navigation/navigationPresentationMixin.js";
import navigationDisplayMixin from "@/scripts/sections/common/navigation/navigationDisplayMixin.js";
import navigationDataMixin from "@/scripts/sections/common/navigation/navigationDataMixin.js";
import {mapGetters} from "vuex";
import {getPortfolioGenerationPreviewPath} from "@/scripts/view/portfolio/generationPreview.js";
import {
    PageTypesEnum,
} from "@/scripts/common/enums.js";

export default {
    name: "PortfolioNavigationBar",
    mixins: [stickyNavigationMixin, navigationWrapObserverMixin, navigationPresentationMixin, navigationDisplayMixin, navigationDataMixin],
    components: {
        PortfolioNavigationLogo,
        PortfolioNavigationItem,
        PortfolioNavigationSocialLinks,
        HamburgerToggle,
        AnimationWrapper,
        CtaButton,
    },
    props: {
        navigation: {
            type: Object,
            default: null,
        },
        hideLogo: {
            type: Boolean,
            default: false,
        },
        hideHome: {
            type: Boolean,
            default: false,
        },
        inline: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        ...mapGetters("design", ["animationSettings"]),
        portfolio() {
            return this.viewData?.portfolio;
        },
        project() {
            return this.viewData?.project || this.$store.state.project;
        },
        isCaseStudy() {
            return this.project?.type === PageTypesEnum.CASE_STUDY;
        },
        usingGlobalColorStyles() {
            return this.project?.type === PageTypesEnum.CASE_STUDY && this.project?.designSettings?.globalColorStyles;
        },
        colorPalette() {
            return this.isCaseStudy && !this.usingGlobalColorStyles
                ? this.project?.designSettings?.colorPalette
                : this.portfolio?.designSettings?.colorPalette;
        },
        theme() {
            return this.portfolio?.templateSettings?.theme || "";
        },
        logo() {
            return this.portfolio?.logo || null;
        },
        userName() {
            return this.logo?.name || this.viewData?.user?.name || "";
        },
        allPagesForNavigation() {
            return this.viewData?.portfolioPages || [];
        },
        socialLinksForNavigation() {
            return this.portfolio?.socialLinks || [];
        },
    },
    methods: {
        rootIsOwnDomain() {
            return this.portfolio?.ownDomainAddress && location.origin.includes(this.portfolio.ownDomainAddress);
        },
        navigateToHome() {
            if (!this.portfolio?.shortId) return;
            if (this.portfolio.ownDomainAddress) {
                window.location.assign(`https://${this.portfolio.ownDomainAddress}`);
                return;
            }

            const homeRoute = getPortfolioGenerationPreviewPath(this.$route)
                || (this.rootIsOwnDomain() ? "/" : `/${this.portfolio.shortId}`);
            this.$router.push(homeRoute);
            this.viewData.navOpen = false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/styles/layouts/new-portfolio-layout/components/navbar";
</style>
